import { ref, watchEffect, onUnmounted, computed, type Ref } from 'vue';

/**
 * Scale embedded demos to fit a narrow doc column without overflow clipping.
 */
export function useScaledEmbedResize(
  embeddedRef: Ref<boolean>,
  intrinsicW: number,
  intrinsicH: number,
  opts: { minScale?: number; maxScale?: number } = {}
) {
  const containerRef = ref<HTMLElement | null>(null);
  const scale = ref(0.5);
  const minS = opts.minScale ?? 0.28;
  const maxS = opts.maxScale ?? 0.68;

  let ro: ResizeObserver | null = null;

  function compute() {
    const el = containerRef.value;
    if (!el) return;
    const cw = el.clientWidth;
    if (cw < 1) return;
    const s = Math.min(maxS, cw / intrinsicW);
    scale.value = Math.max(minS, s);
  }

  watchEffect((onCleanup) => {
    if (!embeddedRef.value || !containerRef.value) return;
    ro = new ResizeObserver(() => compute());
    ro.observe(containerRef.value);
    compute();
    onCleanup(() => {
      ro?.disconnect();
      ro = null;
    });
  });

  onUnmounted(() => {
    ro?.disconnect();
    ro = null;
  });

  const innerStyle = computed(() => {
    const s = scale.value;
    return {
      width: `${intrinsicW}px`,
      height: `${intrinsicH}px`,
      transform: `scale(${s})`,
      transformOrigin: 'top left',
      marginRight: `${-intrinsicW * (1 - s)}px`,
      marginBottom: `${-intrinsicH * (1 - s)}px`,
    };
  });

  return { containerRef, scale, innerStyle };
}
